import { useState } from "react";
import CodelcoLogo from "./CodelcoLogo";

const links = [
  { label: "Inicio", id: "inicio" },
  {
    label: "Aranceles y Protocolos",
    id: "info-dental",
    children: [
      { label: "Protocolos", id: "protocolo" },
      { label: "Aranceles", id: "aranceles" },
    ],
  },
  { label: "Odontólogos Tratantes", id: "odontologos" },
  { label: "Beneficios Codelco", id: "beneficios" },
  { label: "Descargas y Links", id: "descargas" },
];

function isGroupActive(active, link) {
  return link.children?.some((child) => child.id === active);
}

export default function Navbar({ active, onNav }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false);

  const handleNav = (pageId) => {
    onNav(pageId);
    setMenuOpen(false);
    setMobileSubmenuOpen(false);
  };

  return (
    <nav className="bg-navy-600 h-16 flex items-center px-4 md:px-5 sticky top-0 z-50 shadow-lg">
      <div className="flex items-center gap-0 mr-5 flex-shrink-0">
        <button
          onClick={() => handleNav("inicio")}
          className="group flex items-center rounded-full bg-white/95 px-3 py-1.5 ring-1 ring-white/15 shadow-[0_4px_14px_rgba(0,0,0,0.12)] transition-all duration-200 hover:bg-white hover:-translate-y-0.5"
          aria-label="Ir a inicio"
        >
          <CodelcoLogo className="h-6 md:h-7 w-auto" />
        </button>
        <div className="text-white/70 text-[11px] tracking-wide ml-3 border-l border-white/15 pl-3 whitespace-nowrap hidden sm:block">
          Contraloría Dental
        </div>
      </div>

      <div className="hidden lg:flex items-center gap-1 ml-auto">
        {links.map((link) => {
          const activeClass =
            active === link.id || isGroupActive(active, link) ? "nav-link-active" : "";

          if (!link.children) {
            return (
              <button
                key={link.id}
                onClick={() => handleNav(link.id)}
                className={`nav-link ${activeClass}`}
              >
                {link.label}
              </button>
            );
          }

          return (
            <div key={link.id} className="relative group">
              <button className={`nav-link inline-flex items-center gap-1 ${activeClass}`}>
                {link.label}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-current">
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div className="absolute left-0 top-full pt-2 hidden group-hover:block group-focus-within:block">
                <div className="min-w-[190px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
                  {link.children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => handleNav(child.id)}
                      className={`block w-full px-4 py-3 text-left text-sm transition-colors ${
                        active === child.id
                          ? "bg-codelco/5 text-codelco"
                          : "text-gray-600 hover:bg-gray-50 hover:text-navy-600"
                      }`}
                    >
                      {child.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
        <button
          onClick={() => handleNav("solicitudes")}
          className="ml-1 bg-codelco text-white text-[11px] font-medium px-3 py-1.5 rounded-md hover:bg-codelco-dark transition-colors whitespace-nowrap"
        >
          Ingreso de Solicitudes →
        </button>
      </div>

      <button
        className="ml-auto lg:hidden text-white/70 hover:text-white"
        onClick={() => setMenuOpen((prev) => !prev)}
      >
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path
            d={menuOpen ? "M4 4l14 14M18 4L4 18" : "M3 6h16M3 11h16M3 16h16"}
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {menuOpen && (
        <div className="absolute top-14 left-0 right-0 bg-navy-700 border-t border-white/10 py-2 lg:hidden z-50">
          {links.map((link) => {
            if (!link.children) {
              return (
                <button
                  key={link.id}
                  onClick={() => handleNav(link.id)}
                  className={`block w-full px-5 py-2.5 text-left text-sm transition-colors ${
                    active === link.id
                      ? "bg-white/10 text-white"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {link.label}
                </button>
              );
            }

            const groupActive = isGroupActive(active, link);

            return (
              <div key={link.id}>
                <button
                  onClick={() => setMobileSubmenuOpen((prev) => !prev)}
                  className={`flex w-full items-center justify-between px-5 py-2.5 text-left text-sm transition-colors ${
                    groupActive
                      ? "bg-white/10 text-white"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <span>{link.label}</span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    className={`transition-transform ${mobileSubmenuOpen ? "rotate-180" : ""}`}
                  >
                    <path
                      d="M3 5L7 9L11 5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {mobileSubmenuOpen && (
                  <div className="bg-navy-800/60 border-y border-white/5 py-1">
                    {link.children.map((child) => (
                      <button
                        key={child.id}
                        onClick={() => handleNav(child.id)}
                        className={`block w-full px-8 py-2.5 text-left text-sm transition-colors ${
                          active === child.id
                            ? "text-codelco-light"
                            : "text-white/65 hover:text-white hover:bg-white/5"
                        }`}
                      >
                        {child.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <button
            onClick={() => handleNav("solicitudes")}
            className="block w-full text-left text-codelco-light font-medium text-sm px-5 py-2.5 hover:bg-white/5"
          >
            Ingreso de Solicitudes →
          </button>
        </div>
      )}
    </nav>
  );
}
